/**
 * The URL state of a collection table, read the same way by the server component that requests one
 * page (`page.tsx`) and by the client hook that writes it (`use-table-filters.ts`). Plain functions,
 * not a hook: a server component cannot call into a `'use client'` module.
 *
 * Filters are `?f.<prefix>_<field>=a,b`; a server-mode table (#842) adds `page`, `pageSize`, `sort`
 * (API field names, `-issued`) and `q` (design.md §8).
 */

export type TableFilters = Record<string, string[]>

export const GENERAL_FILTERS_PREFIX = 'f.'

/** The largest id the API takes (`MAX_ID`, a Postgres `integer`). */
const MAX_ID = 2_147_483_647

/** The cookie that keeps the year the user picked (`useYear`); read on the server too (#862). */
export const YEAR_COOKIE = 'year'
/** "Ewig": every year. */
export const ALL_YEARS = -1

/** The years the API takes (`Year` in `backend/app/schemas/base.py`). */
const MIN_YEAR = 2000
const MAX_YEAR = 2100

const yearOf = (raw: string | null | undefined): number | undefined => {
  if (raw === String(ALL_YEARS)) return ALL_YEARS
  return raw && /^\d{4}$/.test(raw) && Number(raw) >= MIN_YEAR && Number(raw) <= MAX_YEAR ? Number(raw) : undefined
}

/**
 * The year a page shows: `?year=`, else the `year` cookie, else the current year (#862). `-1` is
 * every year. Anything the API would refuse counts as absent, so it never becomes an error page.
 * The server pages and `useYear` both use it, so the selector shows the year the page used.
 */
export const resolveYear = (raw: string | null | undefined, cookie: string | null | undefined, now = new Date()): number =>
  yearOf(raw) ?? yearOf(cookie) ?? now.getFullYear()

/** The year as a list filter: none for every year. */
export const yearFilter = (year: number): number | undefined => (year === ALL_YEARS ? undefined : year)

/**
 * The year of a page that needs one (a salary, a Lohnausweis, the members overview): "Ewig", which
 * the cookie keeps since #862, is the current year.
 */
export const oneYear = (year: number, now = new Date()): number => (year === ALL_YEARS ? now.getFullYear() : year)

/** An id the API takes, or `undefined`. */
export const urlId = (raw: string | null | undefined): number | undefined => {
  if (!raw || !/^\d{1,10}$/.test(raw)) return undefined
  const id = Number(raw)
  return id >= 1 && id <= MAX_ID ? id : undefined
}

/** The URL values of an id filter that the API takes; the rest are dropped, not sent. */
export const urlIds = (values: string[] = []): number[] => values.map(urlId).filter((id): id is number => id !== undefined)

/** The URL values of an enum filter that are members of `values` (a `strEnum`); the rest are dropped. */
export const urlEnum = <T extends string>(values: Record<T, T>, raw: string[] = []): T[] =>
  raw.filter((v): v is T => Object.hasOwn(values, v))

/** The page sizes a server-mode table offers; the contract caps a page at 200 (design.md §8.1). */
export const PAGE_SIZES = [10, 20, 50, 100, 200] as const
export const DEFAULT_PAGE_SIZE = 10

/** The page a server-mode table asks for: the paging, sort and search params of design.md §8. */
export type ListPage = {
  page: number
  pageSize: number
  /** API sort keys, `-issued,name`. */
  sort?: string
  q?: string
}

/** The URL keys of a server-mode table besides its filters. */
export const LIST_KEYS = { page: 'page', pageSize: 'pageSize', sort: 'sort', q: 'q' } as const

/** A server component's `searchParams` as `URLSearchParams`; a repeated key keeps its first value. */
export const toSearchParams = (record: Record<string, string | string[] | undefined> = {}): URLSearchParams => {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(record)) {
    const first = Array.isArray(value) ? value[0] : value
    if (first !== undefined) params.set(key, first)
  }
  return params
}

/**
 * The URL value of a cleared facet that has a default (#864): "every row", so the default does not
 * come back. It is no id, so the API never gets it as a filter.
 */
export const FILTER_ALL = 'all'

/** The URL has a value for the facet `key`, the marker included: no default applies. */
export const hasUrlFilter = (filters: TableFilters, key: string): boolean => (filters[key]?.length ?? 0) > 0

/**
 * What the facets show: the URL's values without the marker, and a default where the URL has no
 * value. A facet with a default holds ids: a value the API would drop is not shown either.
 */
export function effectiveFilters(url: TableFilters, defaults: TableFilters = {}): TableFilters {
  const result: TableFilters = {}
  for (const [key, values] of Object.entries(defaults)) if (values.length && !hasUrlFilter(url, key)) result[key] = values
  for (const [key, values] of Object.entries(url)) {
    const shown = values.filter((v) => v !== FILTER_ALL && (!(key in defaults) || urlId(v) !== undefined))
    if (shown.length) result[key] = shown
  }
  return result
}

/** The URL value after a facet change: an emptied facet with a default is the marker, not "no value". */
export const nextFilterValue = (key: string, values: string[], defaults: TableFilters = {}): string | undefined => {
  const picked = values.filter((v) => v !== FILTER_ALL)
  if (picked.length) return picked.join(',')
  return defaults[key]?.length ? FILTER_ALL : undefined
}

/** `?f.booking_type=a,b` → `{ booking_type: ['a', 'b'] }`, for the filters of one table. */
export function readFilters(params: URLSearchParams, prefix: string): TableFilters {
  const full = `${GENERAL_FILTERS_PREFIX}${prefix}`
  const result: TableFilters = {}
  params.forEach((value, key) => {
    if (key.startsWith(full) && value) result[key.substring(GENERAL_FILTERS_PREFIX.length)] = value.split(',').filter(Boolean)
  })
  return result
}

/** No table has this many pages; the cap keeps the API's `OFFSET` inside a `bigint`. */
const MAX_PAGE = 1_000_000

const positiveInt = (raw: string | null): number | undefined => {
  if (!raw || !/^\d{1,7}$/.test(raw)) return undefined
  const value = Number(raw)
  return value >= 1 && value <= MAX_PAGE ? value : undefined
}

/**
 * The page a server-mode table shows for this URL. Anything the URL holds that the API would refuse
 * falls back to the default instead of failing the page: an unknown page size, a sort key outside
 * `sortable`, a search that is empty after the trim. A page past the end is the API's empty page.
 */
export function readListPage(params: URLSearchParams, sortable: readonly string[]): ListPage {
  const size = positiveInt(params.get(LIST_KEYS.pageSize))
  const sort = (params.get(LIST_KEYS.sort) ?? '')
    .split(',')
    .filter((key) => sortable.includes(key.replace(/^-/, '')))
    .join(',')
  const q = params.get(LIST_KEYS.q)?.trim().slice(0, 100)
  return {
    page: positiveInt(params.get(LIST_KEYS.page)) ?? 1,
    pageSize: size && (PAGE_SIZES as readonly number[]).includes(size) ? size : DEFAULT_PAGE_SIZE,
    sort: sort || undefined,
    q: q || undefined,
  }
}

/** The page and the filters of one table, as its server component requests them. */
export const listQuery = (params: URLSearchParams, prefix: string, sortable: readonly string[]) => ({
  page: readListPage(params, sortable),
  filters: readFilters(params, prefix),
})

/**
 * The URL after a change of `key`: the change is applied, and the page goes back to 1 unless the
 * change is the page itself. A filter, a search, a sort or a page size makes page 5 meaningless.
 */
export function withChange(params: URLSearchParams, key: string, value: string | undefined): URLSearchParams {
  const next = new URLSearchParams(params)
  if (value) next.set(key, value)
  else next.delete(key)
  if (key !== LIST_KEYS.page) next.delete(LIST_KEYS.page)
  return next
}

/** The table's sort state for the URL's API sort keys: `-issued` → `[{ id: 'booking_issued', desc: true }]`. */
export const sortingOf = (sort: string | null, prefix: string): { id: string; desc: boolean }[] =>
  (sort ?? '')
    .split(',')
    .filter(Boolean)
    .map((key) => ({ id: `${prefix}_${key.replace(/^-/, '')}`, desc: key.startsWith('-') }))

/** The URL's API sort keys for the table's sort state; the inverse of {@link sortingOf}. */
export const sortOf = (sorting: { id: string; desc: boolean }[], prefix: string): string | undefined =>
  sorting.map(({ id, desc }) => `${desc ? '-' : ''}${id.replace(new RegExp(`^${prefix}_`), '')}`).join(',') || undefined
